// App.js
import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { UserContext } from './contexts/UserContext';
import { registerLoadingHandlers } from './services/axiosInstance';
import GlobalLoadingBar from './components/GlobalLoadingBar';
import Login from './pages/Login';
import { Logout } from './pages/Logout';
import Register from './pages/Register';
import Home from './pages/Home';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import PermissionGate from './components/PermissionGate';
import CreateOrganization from './pages/CreateOrganization';
import CreateDepartment from './pages/CreateDepartment';
import ProductAdmin from './pages/ProductAdmin';
import SupplierAdmin from './pages/SupplierAdmin';
import ProductAttributeConfig from './pages/ProductAttributeConfig';
import PasswordChange from './pages/PasswordChange';
import Inventory from './pages/Inventory';
import InventoryBulk from './pages/InventoryBulk';
import AuditInventory from './pages/AuditInventory';
import Customers from './pages/Customer';
import Prescriptions from './pages/Prescriptions';
import InvoiceList from './pages/InvoiceList';
import CustomerDashboard from './pages/CustomerDashboard';
import MoveOrders from './pages/MoveOrders';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import AccountPage from './pages/AccountPage';
import ResetPassword from './components/ResetPassword';
import OnboardingCsvUpload from './pages/OnboardingCsvUpload';
import OnboardingSupplierUpload from './pages/OnboardingSupplierUpload';
import SupplierInvoices from './pages/SupplierInvoices';
import SupplierPayments from './pages/SupplierPayments';
import CreditNotes from './pages/CreditNotes';
import RefillReminders from './pages/RefillReminders';
import ManualRefillReminders from './pages/ManualRefillReminders';
import BroadcastSms from './pages/BroadcastSms';
import CustomerPayments from './pages/CustomerPayments';
import ProductPriceList from './pages/ProductPriceList';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import PrintersPage from './pages/PrintersPage';
import PrinterAssignmentsPage from './pages/PrinterAssignmentsPage';
import AccountingDashboard from './pages/AccountingDashboard';
import RefillRequests from './pages/RefillRequests';
import PrescriptionUploads from './pages/PrescriptionUploads';
import IncomingClinicPrescriptions from './pages/IncomingClinicPrescriptions';
import RoleManagement from './pages/RoleManagement';
import ReportsPage from './pages/ReportsPage';
import { ConfirmDialog } from 'primereact/confirmdialog';

const AccessDenied = () => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '60vh', gap: '12px', color: '#6b7280'
    }}>
        <span style={{ fontSize: '2.5rem' }}>🔒</span>
        <h2 style={{ margin: 0, color: '#374151' }}>Access Restricted</h2>
        <p style={{ margin: 0 }}>You don't have permission to view this page.</p>
        <p style={{ margin: 0, fontSize: '0.85rem' }}>
            Contact your pharmacy admin to request access.
        </p>
    </div>
);

const GuardedRoute = ({ codename, children }) => (
    <PermissionGate codename={codename} fallback={<AccessDenied />}>
        {children}
    </PermissionGate>
);

// Mirrors the perms.isAdmin check in Layout.js (Name?.isAdmin || Name?.username === 'gfadmin'),
const AdminOnlyRoute = ({ children }) => {
    const user = useContext(UserContext);
    if (!user) return null;
    const isAdmin = user?.isAdmin || user?.username === 'gfadmin';
    return isAdmin ? children : <AccessDenied />;
};

// Stricter than AdminOnlyRoute: restricted to the gfadmin account specifically
// (Coffer Smart staff performing pharmacy onboarding), not every org's own isAdmin user.
const GfAdminOnlyRoute = ({ children }) => {
    const user = useContext(UserContext);
    if (!user) return null;
    return user?.username === 'gfadmin' ? children : <AccessDenied />;
};

// Registers axios loading handlers once, inside LoadingProvider scope
const AppInner = ({ children }) => {
    const { increment, decrement } = useLoading();
    React.useEffect(() => {
        registerLoadingHandlers(increment, decrement);
    }, [increment, decrement]);
    return children;
};

function App() {
    return (
        <LoadingProvider>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <BreadcrumbProvider>
                <Router>
                <AppInner>
                    <Routes>
                        {/* Public routes */}
                        <Route path="/login"                            element={<Login />} />
                        <Route path="/logout"                           element={<Logout />} />
                        <Route path="/password-change"                  element={<PasswordChange />} />
                        <Route path="/reset-password/:uid/:token"       element={<ResetPassword />} />

                        {/* Protected routes */}
                        <Route element={<PrivateRoute />}>
                            <Route element={<Layout />}>

                                {/* ── Always accessible ─────────────────────────────── */}
                                <Route path="/"         element={<Home />} />
                                <Route path="/home"     element={<Home />} />
                                <Route path="/my-account" element={<AccountPage />} />

                                {/* ── Admin only (existing is_admin flag — now enforced at the route level too) ── */}
                                <Route path="/create-organization"  element={<GfAdminOnlyRoute><CreateOrganization /></GfAdminOnlyRoute>} />
                                <Route path="/departments"          element={<AdminOnlyRoute><CreateDepartment /></AdminOnlyRoute>} />
                                <Route path="/register"             element={<AdminOnlyRoute><Register /></AdminOnlyRoute>} />
                                <Route path="/orgsettings"          element={<AdminOnlyRoute><OrganizationSettingsPage /></AdminOnlyRoute>} />
                                <Route path="/onboarding-upload"    element={<GfAdminOnlyRoute><OnboardingCsvUpload /></GfAdminOnlyRoute>} />
                                <Route path="/onboarding-upload-sup" element={<GfAdminOnlyRoute><OnboardingSupplierUpload /></GfAdminOnlyRoute>} />
                                <Route path="/printers"             element={<AdminOnlyRoute><PrintersPage /></AdminOnlyRoute>} />
                                <Route path="/printer-assignments"  element={<AdminOnlyRoute><PrinterAssignmentsPage /></AdminOnlyRoute>} />
                                <Route path="/roles"                element={<AdminOnlyRoute><RoleManagement /></AdminOnlyRoute>} />  {/* ← NEW */}

                                {/* ── Products ──────────────────────────────────────── */}
                                <Route path="/products" element={
                                    <GuardedRoute codename="view_products">
                                        <ProductAdmin />
                                    </GuardedRoute>
                                } />
                                <Route path="/product-attributes" element={
                                    <GuardedRoute codename="manage_products">
                                        <ProductAttributeConfig />
                                    </GuardedRoute>
                                } />
                                <Route path="/product-pricelists" element={
                                    <GuardedRoute codename="view_price_lists">
                                        <ProductPriceList />
                                    </GuardedRoute>
                                } />

                                {/* ── Suppliers ─────────────────────────────────────── */}
                                <Route path="/suppliers" element={
                                    <GuardedRoute codename="view_suppliers">
                                        <SupplierAdmin />
                                    </GuardedRoute>
                                } />
                                <Route path="/supplier-invoices" element={
                                    <GuardedRoute codename="view_supplier_invoices">
                                        <SupplierInvoices />
                                    </GuardedRoute>
                                } />
                                <Route path="/supplier-payments" element={
                                    <GuardedRoute codename="view_supplier_payments">
                                        <SupplierPayments />
                                    </GuardedRoute>
                                } />

                                {/* ── Inventory ─────────────────────────────────────── */}
                                <Route path="/inventory" element={
                                    <GuardedRoute codename="view_inventory">
                                        <Inventory />
                                    </GuardedRoute>
                                } />
                                <Route path="/inventory-bulk" element={
                                    <GuardedRoute codename="inventory_bulk_upload">
                                        <InventoryBulk />
                                    </GuardedRoute>
                                } />
                                <Route path="/inventory-audit" element={
                                    <GuardedRoute codename="view_inventory_audit">
                                        <AuditInventory />
                                    </GuardedRoute>
                                } />
                                <Route path="/moveorders" element={
                                    <GuardedRoute codename="view_move_orders">
                                        <MoveOrders />
                                    </GuardedRoute>
                                } />

                                {/* ── Customers ─────────────────────────────────────── */}
                                <Route path="/customers" element={
                                    <GuardedRoute codename="view_customers">
                                        <Customers />
                                    </GuardedRoute>
                                } />
                                <Route path="/customers/:id" element={
                                    <GuardedRoute codename="view_customers">
                                        <CustomerDashboard />
                                    </GuardedRoute>
                                } />
                                <Route path="/prescriptions" element={
                                    <GuardedRoute codename="view_prescriptions">
                                        <Prescriptions />
                                    </GuardedRoute>
                                } />
                                <Route path="/invoices" element={
                                    <GuardedRoute codename="view_invoices">
                                        <InvoiceList />
                                    </GuardedRoute>
                                } />
                                <Route path="/customer-payments" element={
                                    <GuardedRoute codename="view_payments">
                                        <CustomerPayments />
                                    </GuardedRoute>
                                } />
                                <Route path="/credit-notes" element={
                                    <GuardedRoute codename="view_credit_notes">
                                        <CreditNotes />
                                    </GuardedRoute>
                                } />
                                <Route path="/refill-reminders" element={
                                    <GuardedRoute codename="view_refill_reminders">
                                        <RefillReminders />
                                    </GuardedRoute>
                                } />
                                <Route path="/manual-refill-reminders" element={
                                    <GuardedRoute codename="view_refill_reminders">
                                        <ManualRefillReminders />
                                    </GuardedRoute>
                                } />
                                <Route path="/broadcast-sms" element={
                                    <GuardedRoute codename="manage_customers">
                                        <BroadcastSms />
                                    </GuardedRoute>
                                } />
                                <Route path="/refill-requests" element={
                                    <GuardedRoute codename="view_refill_requests">
                                        <RefillRequests />
                                    </GuardedRoute>
                                } />
                                <Route path="/prescription-uploads" element={
                                    <GuardedRoute codename="view_prescriptions">
                                        <PrescriptionUploads />
                                    </GuardedRoute>
                                } />
                                <Route path="/clinic-prescriptions" element={
                                    <GuardedRoute codename="view_prescriptions">
                                        <IncomingClinicPrescriptions />
                                    </GuardedRoute>
                                } />

                                {/* ── Finance ───────────────────────────────────────── */}
                                <Route path="/accounting-dashboard" element={
                                    <GuardedRoute codename="view_finance_dashboard">
                                        <AccountingDashboard />
                                    </GuardedRoute>
                                } />

                                {/* ── Advanced Reports ───────────────────────────────── */}
                                <Route path="/reports" element={
                                    <GuardedRoute codename="view_reports">
                                        <ReportsPage />
                                    </GuardedRoute>
                                } />
                            </Route>
                        </Route>
                    </Routes>

                    <ToastContainer
                        position="top-center"
                        autoClose={3000}
                        hideProgressBar
                        newestOnTop
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                    />
                    <ConfirmDialog />
                </AppInner>
                </Router>
            </BreadcrumbProvider>
        </ErrorBoundary>
        <GlobalLoadingBar />
        </LoadingProvider>
    );
}

function ErrorFallback({ error }) {
    return (
        <div>
            <h2>Something went wrong!</h2>
            <p>{error.message}</p>
        </div>
    );
}

export default App;